import { Injectable } from '@angular/core';
import { AuthCredential } from './model/auth-credential.model';
import { AuthRepositoryModule } from './auth-repository.module';

/**
 * Responsible for storing minimal needed credentials for authenticated user.
 * Use with caution. Credentials are stored in the local storage.
 */
@Injectable({ providedIn: AuthRepositoryModule })
export class AuthStorage {
  private static readonly ACCESS_TOKEN: string = 'ACCESS_TOKEN';
  private static readonly REFRESH_TOKEN: string = 'REFRESH_TOKEN';
  private static readonly ID_TOKEN: string = 'ID_TOKEN';
  private static readonly CLIENT_USER_KEY: string = 'clientUser';
  private static readonly CLIENT_PASSWORD_KEY: string = 'clientPassword';

  private credential?: AuthCredential;

  constructor() {}

  public setCredential(credential: AuthCredential) {
    this.credential = credential;
    sessionStorage.setItem(AuthStorage.ACCESS_TOKEN, credential.accessToken);
    sessionStorage.setItem(AuthStorage.REFRESH_TOKEN, credential.refreshToken);
    sessionStorage.setItem(AuthStorage.ID_TOKEN, credential.idToken);
  }

  public getCredential(): AuthCredential | undefined {
    this.credential = this.readCredential();
    return this.credential;
  }

  public get cachedCredential(): AuthCredential {
    return this.credential!;
  }

  /**
   * Remove everithyng from storage.
   */
  public clear() {
    this.clearCache();
    sessionStorage.removeItem(AuthStorage.ACCESS_TOKEN);
    sessionStorage.removeItem(AuthStorage.REFRESH_TOKEN);
    sessionStorage.removeItem(AuthStorage.ID_TOKEN);
  }

  /**
   * Clear cached variables. Next get will try to read from local storage.
   */
  public clearCache() {
    this.credential = undefined;
  }

  private readCredential(): AuthCredential | undefined {
    const accessToken = sessionStorage.getItem(
      AuthStorage.ACCESS_TOKEN
    ) as string;
    const refreshToken = sessionStorage.getItem(
      AuthStorage.REFRESH_TOKEN
    ) as string;
    const idToken = sessionStorage.getItem(AuthStorage.ID_TOKEN) as string;

    if (accessToken && refreshToken && idToken) {
      return new AuthCredential(accessToken, refreshToken, idToken);
    }

    return undefined;
  }

  private readClientAPICredentials(): string {
    const user = atob(
      sessionStorage.getItem(AuthStorage.CLIENT_USER_KEY) as string
    );
    const password = atob(
      sessionStorage.getItem(AuthStorage.CLIENT_PASSWORD_KEY) as string
    );

    return btoa(user + ':' + password);
  }

  public getClientAPIAuthorizationHeader(): string {
    return 'Basic' + ' ' + this.readClientAPICredentials();
  }
}
